import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import {
  SealCheck,
  CalendarBlank,
  Funnel,
  X,
  CurrencyDollar,
  Export,
  MagnifyingGlass,
  Eye
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const CURRENCY_SYMBOLS = {
  USD: '$',
  XCD: 'EC$',
  JMD: 'J$',
  TTD: 'TT$'
};

const CURRENCIES = ['USD', 'XCD', 'JMD', 'TTD'];

const DEPARTMENTS = [
  'Kitchen',
  'Housekeeping',
  'Front Office',
  'Food & Beverage',
  'Maintenance',
  'Spa',
  'Security',
  'Finance',
  'General Management'
];

const ClosedPOsPage = () => {
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAuth();
  const [closedPOs, setClosedPOs] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filters
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filterVendor, setFilterVendor] = useState('all');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterProperty, setFilterProperty] = useState('all');
  const [filterCurrency, setFilterCurrency] = useState('all');
  const [filterOnTime, setFilterOnTime] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  const isMultiProperty = user?.subscription_tier === 'multi_property';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [closedRes, vendorsRes] = await Promise.all([
        axios.get(`${API_URL}/api/closed-pos`, getAuthHeaders()),
        axios.get(`${API_URL}/api/vendors`, getAuthHeaders())
      ]);
      setClosedPOs(closedRes.data);
      setVendors(vendorsRes.data);
    } catch (error) {
      toast.error('Failed to load closed POs');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate.toISOString());
      if (endDate) params.append('end_date', endDate.toISOString());
      if (filterVendor !== 'all') params.append('vendor_id', filterVendor);
      if (filterDepartment !== 'all') params.append('department', filterDepartment);
      if (filterProperty !== 'all') params.append('property_id', filterProperty);
      if (filterCurrency !== 'all') params.append('currency', filterCurrency);
      if (filterOnTime !== 'all') params.append('on_time', filterOnTime);
      
      const response = await axios.get(`${API_URL}/api/closed-pos?${params.toString()}`, getAuthHeaders());
      setClosedPOs(response.data);
    } catch (error) {
      toast.error('Failed to apply filters');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setFilterVendor('all');
    setFilterDepartment('all');
    setFilterProperty('all');
    setFilterCurrency('all');
    setFilterOnTime('all');
    setSearchQuery('');
    fetchData();
  };

  const hasActiveFilters = startDate || endDate || filterVendor !== 'all' || filterDepartment !== 'all' || filterProperty !== 'all' || filterCurrency !== 'all' || filterOnTime !== 'all';

  const formatCurrency = (value, currency) => {
    const symbol = CURRENCY_SYMBOLS[currency] || '$';
    return `${symbol}${value.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  };

  // Filter by search query
  const filteredPOs = closedPOs.filter(po =>
    po.po_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
    po.vendor_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (po.items_summary && po.items_summary.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Calculate totals - use ALL filtered POs for summary cards
  const totalValue = filteredPOs.reduce((sum, po) => sum + po.total_value, 0);
  const totalCount = filteredPOs.length;

  const exportToCSV = () => {
    const headers = ['PO Number', 'Vendor', 'Items Summary', 'Total Value', 'Currency', 'Delivery Date', 'Close Date', 'Freight Forwarder', 'ETA', 'Department'];
    const rows = filteredPOs.map(po => [
      po.po_number,
      po.vendor_name,
      po.items_summary || '',
      po.total_value.toFixed(2),
      po.currency,
      new Date(po.required_delivery_date).toLocaleDateString(),
      new Date(po.closed_at).toLocaleDateString(),
      po.freight_forwarder_name || 'N/A',
      po.original_eta ? new Date(po.original_eta).toLocaleDateString() : '-',
      po.department || ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `closed_pos_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    toast.success('Report exported successfully');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#2D6A4F] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Closed POs
          </h1>
          <p className="text-slate-500 mt-1">View and analyze completed purchase orders</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className={hasActiveFilters ? 'border-[#2D6A4F] text-[#2D6A4F]' : ''}
            data-testid="toggle-filters-btn"
          >
            <Funnel size={18} className="mr-2" />
            Filters
            {hasActiveFilters && (
              <span className="ml-2 w-5 h-5 rounded-full bg-[#2D6A4F] text-white text-xs flex items-center justify-center">
                !
              </span>
            )}
          </Button>
          <Button
            onClick={exportToCSV}
            className="bg-[#2D6A4F] hover:bg-[#1E4D38]"
            data-testid="export-csv-btn"
          >
            <Export size={18} className="mr-2" />
            Export to CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards - Side by Side */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-[#2D6A4F]/10 flex items-center justify-center">
              <SealCheck size={28} weight="duotone" className="text-[#2D6A4F]" />
            </div>
            <div>
              <p className="text-3xl font-bold text-slate-800">{totalCount}</p>
              <p className="text-sm text-slate-500">Total Closed POs</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-[#0F52BA]/10 flex items-center justify-center">
              <CurrencyDollar size={28} weight="duotone" className="text-[#0F52BA]" />
            </div>
            <div>
              <p className="text-3xl font-bold text-slate-800">${totalValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              <p className="text-sm text-slate-500">Total Value</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6" data-testid="filters-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800">Filter Options</h3>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500">
                <X size={16} className="mr-1" />
                Clear All
              </Button>
            )}
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
            {/* Date Range - Start */}
            <div className="space-y-2">
              <Label>From Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left" data-testid="start-date-picker">
                    <CalendarBlank size={18} className="mr-2" />
                    {startDate ? format(startDate, 'MMM d, yyyy') : 'Select'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Date Range - End */}
            <div className="space-y-2">
              <Label>To Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left" data-testid="end-date-picker">
                    <CalendarBlank size={18} className="mr-2" />
                    {endDate ? format(endDate, 'MMM d, yyyy') : 'Select'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={setEndDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Vendor Filter */}
            <div className="space-y-2">
              <Label>Vendor</Label>
              <Select value={filterVendor} onValueChange={setFilterVendor}>
                <SelectTrigger data-testid="filter-vendor-select">
                  <SelectValue placeholder="All Vendors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Vendors</SelectItem>
                  {vendors.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Department Filter */}
            <div className="space-y-2">
              <Label>Department</Label>
              <Select value={filterDepartment} onValueChange={setFilterDepartment}>
                <SelectTrigger data-testid="filter-department-select">
                  <SelectValue placeholder="All Depts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {DEPARTMENTS.map(dept => (
                    <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Currency Filter */}
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select value={filterCurrency} onValueChange={setFilterCurrency}>
                <SelectTrigger data-testid="filter-currency-select">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Currencies</SelectItem>
                  {CURRENCIES.map(curr => (
                    <SelectItem key={curr} value={curr}>{curr}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* On Time Filter */}
            <div className="space-y-2">
              <Label>On Time</Label>
              <Select value={filterOnTime} onValueChange={setFilterOnTime}>
                <SelectTrigger data-testid="filter-ontime-select">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="yes">On Time</SelectItem>
                  <SelectItem value="no">Late</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Property Filter (only for multi-property users) */}
            {isMultiProperty && (
              <div className="space-y-2">
                <Label>Property</Label>
                <Select value={filterProperty} onValueChange={setFilterProperty}>
                  <SelectTrigger data-testid="filter-property-select">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Properties</SelectItem>
                    <SelectItem value="resort-1">Resort 1</SelectItem>
                    <SelectItem value="resort-2">Resort 2</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="mt-4 flex justify-end">
            <Button onClick={applyFilters} className="bg-[#2D6A4F] hover:bg-[#1E4D38]" data-testid="apply-filters-btn">
              Apply Filters
            </Button>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="relative max-w-md">
        <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Search by PO number, vendor, or items..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="closed-pos-search"
        />
      </div>

      {/* Closed POs Table */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        {filteredPOs.length === 0 ? (
          <div className="text-center py-16">
            <SealCheck size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No closed purchase orders found</p>
            {hasActiveFilters && (
              <Button variant="link" onClick={clearFilters} className="mt-2 text-[#2D6A4F]">
                Clear filters
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">PO Number</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Vendor</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Items Summary</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Total Value</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Currency</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Delivery Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Close Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Freight Forwarder</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">ETA</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredPOs.map(po => (
                  <tr key={po.id} className="hover:bg-slate-50/50" data-testid={`closed-po-row-${po.id}`}>
                    <td className="px-4 py-4">
                      <p className="font-medium text-slate-800">{po.po_number}</p>
                      {po.department && (
                        <p className="text-xs text-slate-500">{po.department}</p>
                      )}
                    </td>
                    <td className="px-4 py-4 text-slate-600">{po.vendor_name}</td>
                    <td className="px-4 py-4 text-slate-600 max-w-[200px]">
                      <p className="truncate text-sm" title={po.items_summary}>
                        {po.items_summary || '-'}
                      </p>
                    </td>
                    <td className="px-4 py-4 font-medium text-slate-800">
                      {formatCurrency(po.total_value, po.currency)}
                    </td>
                    <td className="px-4 py-4 text-slate-600">{po.currency}</td>
                    <td className="px-4 py-4 text-slate-600">
                      {new Date(po.required_delivery_date).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric'
                      })}
                    </td>
                    <td className="px-4 py-4 text-slate-600">
                      {new Date(po.closed_at).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric'
                      })}
                    </td>
                    <td className="px-4 py-4 text-slate-600">
                      {po.freight_forwarder_name || <span className="text-slate-400">-</span>}
                    </td>
                    <td className="px-4 py-4 text-slate-600">
                      {po.original_eta ? (
                        new Date(po.original_eta).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric'
                        })
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/purchase-orders/${po.id}`)}
                        className="text-[#0F52BA] hover:text-[#0A3D8F] hover:bg-[#0F52BA]/10"
                        data-testid={`view-closed-po-${po.id}`}
                      >
                        <Eye size={18} className="mr-1" />
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Footer with total count */}
      {filteredPOs.length > 0 && (
        <div className="text-sm text-slate-500 text-right">
          Showing {filteredPOs.length} closed purchase order(s) • Total value: ${totalValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
        </div>
      )}
    </div>
  );
};

export default ClosedPOsPage;
